/**
 * Which observations a rule (or a sample) looks at, and the filter box text for
 * it. The text uses Langfuse's own column names, so a filter copied from
 * Langfuse works: `traceName:triage type:GENERATION,TOOL name:"extraction run"`.
 * Several values are comma-separated; quotes allow spaces. Any other Langfuse
 * column (`level:ERROR`, `userId:u1`) is kept as it is and sent unchecked:
 * Langfuse decides what it matches. Pure, no React.
 */

import type { TraceFilters } from "./types";

/** An empty list means "any"; `root` null means root and non-root alike. */
export interface ObservationFilters {
  traceNames: string[];
  types: string[];
  names: string[];
  root: boolean | null;
  environments: string[];
  version: string;
  /** Any other Langfuse column, with its values, in the order typed. */
  columns: Record<string, string[]>;
}

export interface TraceQuery extends ObservationFilters {
  skipFailed: boolean;
}

/** A new rule or sample: no trace name yet, every observation, failed runs too. */
export const NEW_QUERY: TraceQuery = {
  traceNames: [],
  types: [],
  names: [],
  root: null,
  environments: [],
  version: "",
  columns: {},
  skipFailed: false,
};

type ListKey = "traceNames" | "types" | "names" | "environments";
const LISTS: Array<[column: string, key: ListKey]> = [
  ["traceName", "traceNames"],
  ["type", "types"],
  ["name", "names"],
];
const COLUMNS = ["traceName", "type", "name", "isRootObservation", "environment", "version"];

const quote = (v: string) => (/[\s,"]/.test(v) ? `"${v.replace(/"/g, "")}"` : v);
const joined = (values: string[]) => values.map(quote).join(",");

/** The filter box text for `f`, columns in a fixed order. */
export function toText(f: ObservationFilters): string {
  const parts = LISTS.filter(([, key]) => f[key].length).map(
    ([column, key]) => `${column}:${joined(f[key])}`,
  );
  if (f.root !== null) parts.push(`isRootObservation:${f.root}`);
  if (f.environments.length) parts.push(`environment:${joined(f.environments)}`);
  if (f.version) parts.push(`version:${quote(f.version)}`);
  for (const [column, values] of Object.entries(f.columns)) parts.push(`${column}:${joined(values)}`);
  return parts.join(" ");
}

export const FILTER_TOKEN = /([A-Za-z][\w.]*):((?:"[^"]*"|[^\s,"]+)(?:,(?:"[^"]*"|[^\s,"]+))*)/g;
const VALUE = /"[^"]*"|[^,]+/g;

/** The filters `text` names, or why it cannot be read. */
export function parse(text: string): { filters: ObservationFilters } | { error: string } {
  const f: ObservationFilters = {
    traceNames: [],
    types: [],
    names: [],
    root: null,
    environments: [],
    version: "",
    columns: {},
  };
  let rest = text;
  for (const [whole, given, list] of text.matchAll(FILTER_TOKEN)) {
    rest = rest.replace(whole, " ");
    const column = COLUMNS.find((c) => c.toLowerCase() === given!.toLowerCase());
    const values = list!.match(VALUE)!.map((v) => v.replace(/^"|"$/g, "").trim());
    const bad = { error: `"${list}" is not a valid value for ${column ?? given}.` };
    if (values.some((v) => !v)) return bad;
    if (!column) {
      // Not one of ours: Langfuse's to judge (a wrong value matches nothing).
      f.columns[given!] = [...new Set([...(f.columns[given!] ?? []), ...values])];
    } else if (column === "isRootObservation") {
      const v = values.length === 1 ? values[0]!.toLowerCase() : "";
      if (v !== "true" && v !== "false") return bad;
      f.root = v === "true";
    } else if (column === "version") {
      if (values.length !== 1 || !values[0]) return bad;
      f.version = values[0];
    } else {
      const key = column === "environment" ? "environments" : LISTS.find(([c]) => c === column)![1];
      const cased = column === "type" ? values.map((v) => v.toUpperCase()) : values;
      f[key] = [...new Set([...f[key], ...cased])];
    }
  }
  if (rest.trim()) return { error: `Could not read "${rest.trim()}". Write filters as column:value.` };
  return { filters: f };
}

const strings = (v: unknown): string[] =>
  (Array.isArray(v) ? v : [v]).filter((x): x is string => typeof x === "string" && x !== "");

/** A saved rule's filters as a query. The shape before observations (`agent`) reads as root observations of that trace name. */
export function fromFilters(saved: Record<string, unknown> | undefined): TraceQuery {
  const f = saved ?? {};
  const legacy = "agent" in f && !("trace_name" in f);
  return {
    traceNames: strings(legacy ? f.agent : f.trace_name),
    types: strings(f.type),
    names: strings(f.name),
    root: legacy ? true : typeof f.is_root === "boolean" ? f.is_root : null,
    environments: strings(f.environment),
    version: typeof f.version === "string" || typeof f.version === "number" ? String(f.version) : "",
    columns: Object.fromEntries(
      Object.entries((f.columns ?? {}) as Record<string, unknown>)
        .map(([c, v]) => [c, strings(v)] as const)
        .filter(([, v]) => v.length),
    ),
    // Failed runs are kept unless asked; the old shape only ever left them out.
    skipFailed: legacy ? f.skip_failed !== false : f.skip_failed === true,
  };
}

/** The query as a rule saves it. */
export function toFilters(q: TraceQuery): TraceFilters {
  return {
    trace_name: q.traceNames,
    type: q.types,
    name: q.names,
    is_root: q.root,
    environment: q.environments,
    version: q.version || null,
    skip_failed: q.skipFailed,
    columns: q.columns,
  };
}

/** The query as `/trace-sources/{id}/…` params (lists repeat the param). */
export function toParams(q: TraceQuery) {
  return {
    trace_name: q.traceNames,
    type: q.types,
    name: q.names,
    is_root: q.root ?? undefined,
    environment: q.environments,
    version: q.version || undefined,
    skip_failed: q.skipFailed,
    column: Object.entries(q.columns).flatMap(([c, values]) => values.map((v) => `${c}:${v}`)),
  };
}
